import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sparkle, UploadSimple, ArrowsClockwise, FloppyDisk, Trash, CheckCircle, FileText,
  ArrowLeft, Plus, Flag, ArrowsCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * Embedded AI extraction dialog for a single module.
 *
 * Props:
 *   module: "compliance" | "projects" | "hr" | "assets"
 *   trigger: ReactNode (the button that opens the dialog)
 *   onSaved?: (record) => void   // called after a successful save
 *
 * Behaviour:
 *   1. Upload a PDF / DOCX / TXT / image
 *   2. Claude extracts structured fields
 *   3. User reviews and edits in the same dialog
 *   4. On save, record goes straight into this module's collection
 *   5. Dialog closes and calls onSaved(record) so the host can refresh +
 *      set ?new=<id> to trigger the shared row-highlight hook.
 */

const MODULE_LABEL = {
  compliance: "Compliance",
  projects: "Project",
  hr: "Staff",
  assets: "Asset",
  procurement: "Procurement opportunity",
  audit_report: "Audit baseline",
  bank_statement: "Bank statement",
  subcontractor: "Subcontractor",
};

const MODULE_HINT = {
  compliance: "Upload a licence, certificate, permit, registration, tax clearance, or insurance document.",
  projects: "Upload a contract, award letter, scope document, or project brief. The AI will also break out deliverables, milestones, and timelines.",
  hr: "Upload a CV, offer letter, employment contract, or appointment letter.",
  assets: "Upload a delivery note, calibration certificate, rental agreement, or purchase record.",
  procurement: "Upload an RFP, tender notice, or expression-of-interest document. The AI will draft an opportunity capture (F-PROC-01) including stream, value, and key dates.",
  audit_report: "Upload the signed annual audit report (PDF). The AI will extract revenue / costs / net profit / balance-sheet figures and store them as a verified baseline for that fiscal year.",
  bank_statement: "Upload a bank statement (PDF/image). The AI will extract balances and adjustments — these prefill the Bank Reconciliation form for that bank & period.",
  subcontractor: "Upload a subcontractor's company profile, registration pack or signed agreement. The AI extracts the company details, banking and contract terms — high-risk fields must be confirmed against the document before saving.",
};

const FIELD_LABELS = {
  title: "Title", name: "Project name", full_name: "Full name",
  category: "Category", authority: "Issuing authority",
  reference_number: "Reference #", issue_date: "Issue date",
  expiry_date: "Expiry date", status: "Status", notes: "Notes",
  code: "Project code / contract ref", client: "Client", description: "Scope summary",
  start_date: "Start date", end_date: "End date", budget: "Contract value (USD)",
  client_expectations: "Client expectations",
  reporting_requirements: "Reporting requirements",
  major_obligations: "Major obligations",
  email: "Email", phone: "Phone", employee_code: "Employee code",
  staff_type: "Staff type", designation: "Designation", department: "Department",
  join_date: "Join date", salary: "Salary (USD)", contract_type: "Contract type",
  asset_id: "Asset ID", serial_number: "Serial #", storage_zone: "Storage zone",
  assigned_to: "Assigned to", calibration_due: "Calibration due",
  // audit_report
  fiscal_year: "Fiscal year", auditor: "Auditor / firm", report_date: "Report date",
  revenue: "Revenue (USD)", project_cost: "Project costs (USD)", overhead: "Overhead (USD)",
  net_profit: "Net profit (USD)", total_assets: "Total assets (USD)",
  total_liabilities: "Total liabilities (USD)", equity: "Equity (USD)",
  cash_and_equivalents: "Cash & equivalents (USD)",
  accounts_receivable: "Accounts receivable (USD)",
  accounts_payable: "Accounts payable (USD)",
  fixed_assets_nbv: "Fixed assets NBV (USD)",
  // bank_statement
  bank_key: "Bank", bank_name: "Bank name", account_number: "Account #",
  period: "Period (YYYY-MM)", opening_balance: "Opening balance (USD)",
  statement_balance: "Statement closing balance (USD)",
  deposits_in_transit: "Deposits in transit (USD)",
  outstanding_cheques: "Outstanding cheques (USD)",
  interest_income: "Interest income (USD)",
  bank_charges: "Bank charges (USD)",
  nsf_items: "NSF items (USD)",
  // subcontractor
  vendor_name: "Company / vendor name", registration_number: "Registration #",
  licence_number: "Licence #", tax_number: "Tax number (TIN)", address: "Address",
  contact_person: "Contact person", account_name: "Account name",
  swift: "SWIFT / BIC", contract_title: "Contract title",
  contract_value: "Contract value (USD)", currency: "Currency",
  effective_date: "Effective date", payment_terms: "Payment terms",
  retention_pct: "Retention %", signatory_name: "Signatory name",
  signatory_title: "Signatory title", scope_summary: "Scope summary",
};

const SELECT_OPTIONS = {
  "compliance.category": ["registration", "license", "permit", "tax_clearance", "certification"],
  "hr.staff_type": ["permanent", "project_based", "contract", "intern"],
  "assets.category": ["CAT-A", "CAT-B", "CAT-C"],
  "bank_statement.bank_key": ["premier", "amal", "salaam", "dahabshiil"],
};

const MULTILINE_FIELDS = new Set([
  "notes", "description", "client_expectations", "reporting_requirements", "major_obligations",
  "scope_summary", "payment_terms", "address",
]);

const NUMERIC_FIELDS = new Set([
  "budget", "salary", "estimated_value", "fiscal_year",
  "contract_value", "retention_pct",
  "revenue", "project_cost", "overhead", "net_profit",
  "total_assets", "total_liabilities", "equity",
  "cash_and_equivalents", "accounts_receivable", "accounts_payable", "fixed_assets_nbv",
  "opening_balance", "statement_balance",
  "deposits_in_transit", "outstanding_cheques",
  "interest_income", "bank_charges", "nsf_items",
]);

const MODULE_FIELDS = {
  compliance: ["title", "category", "authority", "reference_number", "issue_date", "expiry_date", "status", "notes"],
  projects: ["name", "code", "client", "description", "start_date", "end_date", "budget",
             "client_expectations", "reporting_requirements", "major_obligations", "status"],
  hr: ["full_name", "email", "phone", "employee_code", "staff_type", "designation",
       "department", "join_date", "end_date", "salary", "contract_type", "notes"],
  assets: ["asset_id", "description", "category", "serial_number", "storage_zone",
           "assigned_to", "status", "calibration_due", "notes"],
  procurement: ["title", "stream", "counterparty", "estimated_value", "description",
                "source_platform", "tender_id", "closing_date", "method", "selection_method",
                "client_type", "country"],
  audit_report: ["fiscal_year", "auditor", "report_date",
                 "revenue", "project_cost", "overhead", "net_profit",
                 "cash_and_equivalents", "accounts_receivable", "accounts_payable",
                 "fixed_assets_nbv", "total_assets", "total_liabilities", "equity", "notes"],
  bank_statement: ["bank_key", "bank_name", "account_number", "period",
                   "opening_balance", "statement_balance",
                   "deposits_in_transit", "outstanding_cheques",
                   "interest_income", "bank_charges", "nsf_items", "notes"],
  subcontractor: ["vendor_name", "registration_number", "licence_number", "tax_number",
                  "contact_person", "email", "phone", "country", "address",
                  "bank_name", "account_name", "account_number", "swift",
                  "contract_title", "contract_value", "currency",
                  "effective_date", "expiry_date", "payment_terms", "retention_pct",
                  "signatory_name", "signatory_title", "scope_summary"],
};

// Fields shown when mode="renew" — we only care about dates + ref + authority + notes
const RENEWAL_FIELDS = ["reference_number", "authority", "issue_date", "expiry_date", "notes"];


/** Structured deliverable editor used inside the projects review step. */
function DeliverablesEditor({ items, setItems }) {
  const add = () => setItems([...(items || []), { title: "", description: "", due_date: "", milestone: false, dependencies: "" }]);
  const update = (i, k, v) => {
    const c = [...items]; c[i] = { ...c[i], [k]: v }; setItems(c);
  };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));

  const milestoneCount = (items || []).filter((d) => d.milestone).length;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="ai-deliverables-editor">
      <div className="mb-2 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="overline text-[#1E5CBA]">Deliverables & milestones</span>
            {items?.length > 0 && (
              <span className="font-mono-alt text-[10px] text-slate-500">
                {items.length} total · {milestoneCount} milestone{milestoneCount === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <div className="mt-0.5 text-[11px] text-slate-500">
            Ready to become tasks & sub-tasks in the project Tasks Breakdown.
          </div>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid="ai-add-deliverable">
          <Plus size={12} className="mr-1" /> Add
        </Button>
      </div>
      {(!items || items.length === 0) ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-white p-4 text-center text-xs text-slate-500">
          AI found no deliverables in this document. You can add them manually.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((d, i) => (
            <li key={i} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`ai-deliverable-${i}`}>
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-12 md:col-span-7">
                  <Label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">Deliverable</Label>
                  <Input value={d.title || ""} onChange={(e) => update(i, "title", e.target.value)} placeholder="e.g. Detailed Engineering Package" data-testid={`ai-deliv-title-${i}`} />
                </div>
                <div className="col-span-7 md:col-span-3">
                  <Label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">Due date</Label>
                  <Input type="date" value={d.due_date || ""} onChange={(e) => update(i, "due_date", e.target.value)} />
                </div>
                <div className="col-span-3 md:col-span-1">
                  <Label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">Milestone</Label>
                  <button
                    type="button"
                    onClick={() => update(i, "milestone", !d.milestone)}
                    className={`flex h-9 w-full items-center justify-center rounded-md border ${d.milestone ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 bg-white text-slate-400"}`}
                    aria-pressed={d.milestone}
                    title={d.milestone ? "This is a milestone" : "Mark as milestone"}
                  >
                    <Flag size={14} weight={d.milestone ? "fill" : "regular"} />
                  </button>
                </div>
                <div className="col-span-2 md:col-span-1 flex items-end">
                  <Button type="button" variant="outline" size="sm" className="h-9 w-full" onClick={() => remove(i)} aria-label="Remove deliverable">
                    <Trash size={12} />
                  </Button>
                </div>
                <div className="col-span-12">
                  <Label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">Summary / wording</Label>
                  <Textarea rows={2} value={d.description || ""} onChange={(e) => update(i, "description", e.target.value)} placeholder="Professional summary preserved from the contract" />
                </div>
                <div className="col-span-12">
                  <Label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">Dependency / sequencing (optional)</Label>
                  <Input value={d.dependencies || ""} onChange={(e) => update(i, "dependencies", e.target.value)} placeholder="e.g. after M1 approval" />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AiExtractDialog({
  module,
  trigger,
  onSaved,
  mode = "create",     // "create" | "renew"
  existingRecord,      // required when mode === "renew"
  onCustomSave,        // optional: (extractedFields) => Promise<savedRecord> — overrides /ai/save
  saveLabel,           // optional: button text override
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("upload"); // upload | review
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [verified, setVerified] = useState(new Set());   // confirmed high-risk fields
  const [showSource, setShowSource] = useState(true);
  const fileRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [, setSearchParams] = useSearchParams();

  const isRenew = mode === "renew";

  const currentFields = useMemo(() => {
    if (isRenew) return RENEWAL_FIELDS;
    return MODULE_FIELDS[module] || [];
  }, [module, isRenew]);

  // When opening in renewal mode with no upload yet, seed the draft from the
  // existing record so the user can skip upload and just update dates manually.
  useEffect(() => {
    if (!open) return;
    if (isRenew && !draft && existingRecord) {
      setDraft({
        id: null,
        module,
        extracted_fields: {
          reference_number: existingRecord.reference_number || "",
          authority: existingRecord.authority || "",
          issue_date: "",
          expiry_date: "",
          notes: existingRecord.notes || "",
        },
        confidence: null,
        summary: "Enter the new renewal details below, or upload the new document for AI Extract to fill them automatically.",
      });
      setStep("review");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isRenew]);

  const reset = () => {
    setStep("upload"); setFile(null); setDraft(null); setVerified(new Set());
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) setTimeout(reset, 200);
  };

  const onSelectFile = (f) => {
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) return toast.error("File too large (max 15 MB).");
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) onSelectFile(f);
  };

  const extract = async () => {
    if (!file) return toast.error("Please select a file first.");
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("module", module);
      fd.append("file", file);
      const { data } = await api.post("/ai/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDraft(data);
      setStep("review");
      toast.success(`Extracted · ${data.confidence ?? "—"}% confidence`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExtracting(false);
    }
  };

  const updateField = (k, v) => {
    setDraft((d) => ({ ...d, extracted_fields: { ...(d?.extracted_fields || {}), [k]: v } }));
  };

  const save = async () => {
    if (!draft) return;
    const riskList = draft.high_risk_fields || [];
    const present = riskList.filter((f) => (draft.extracted_fields?.[f] ?? "") !== "");
    const unconfirmed = present.filter((f) => !verified.has(f));
    if (unconfirmed.length > 0 && !isRenew && !onCustomSave) {
      toast.error(`Confirm the ${unconfirmed.length} highlighted high-risk field(s) against the document before saving.`);
      return;
    }
    setSaving(true);
    try {
      const clean = { ...(draft.extracted_fields || {}) };
      delete clean.confidence; delete clean.summary;

      let savedRecord;
      if (isRenew) {
        if (!clean.expiry_date) {
          toast.error("New expiry date is required to renew.");
          setSaving(false);
          return;
        }
        savedRecord = (await api.post(`/compliance/${existingRecord.id}/renew`, {
          ...clean,
          ai_extraction_id: draft.id || null,
        })).data;
        // Mark the extraction as saved so history doesn't show it as draft
        if (draft.id) {
          try { await api.post(`/ai/discard/${draft.id}`); } catch { /* ignore */ }
        }
        toast.success(`Renewed · ${savedRecord?.title || MODULE_LABEL[module]}`);
      } else if (onCustomSave) {
        savedRecord = await onCustomSave(clean, draft);
        if (draft.id) {
          try { await api.post(`/ai/discard/${draft.id}`); } catch { /* ignore */ }
        }
      } else {
        const { data } = await api.post("/ai/save", {
          module,
          data: clean,
          extraction_id: draft.id,
          verified_fields: [...verified],
        });
        savedRecord = data.record;
        toast.success(`Saved to ${MODULE_LABEL[module] || module}`);
      }

      handleOpenChange(false);
      // Highlight the record in the host list via ?new=<id>
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("new", savedRecord?.id || "");
        return next;
      }, { replace: true });
      onSaved?.(savedRecord);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const discard = async () => {
    if (draft?.id) {
      try { await api.post(`/ai/discard/${draft.id}`); } catch { /* ignore */ }
    }
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className={`${module === "projects" || module === "subcontractor" ? "sm:max-w-4xl" : "sm:max-w-2xl"} max-h-[92vh] overflow-y-auto`} data-testid={`ai-extract-dialog-${module}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            {isRenew ? <ArrowsCounterClockwise size={18} weight="bold" className="text-[#1E5CBA]" /> : <Sparkle size={18} weight="fill" className="text-[#1E5CBA]" />}
            {isRenew
              ? <>Renew · <span className="truncate">{existingRecord?.title}</span></>
              : <>AI Extract · {MODULE_LABEL[module] || module}</>
            }
          </DialogTitle>
          <DialogDescription>
            {step === "upload"
              ? MODULE_HINT[module]
              : isRenew
                ? "Update the renewal fields — upload the new document for AI Extract to fill them, or enter manually."
                : "Review the extracted fields — edit anything that looks off, then save."}
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`rounded-lg border-2 border-dashed p-8 text-center transition ${
                dragOver ? "border-[#1E5CBA] bg-[#EFF4FC]" : "border-slate-300 bg-slate-50"
              }`}
              data-testid="ai-extract-dropzone"
            >
              <UploadSimple size={26} className="mx-auto text-[#1E5CBA]" />
              <div className="mt-2 text-sm font-medium text-slate-900">
                {file ? file.name : "Drag & drop a PDF, DOCX, TXT, or image"}
              </div>
              <div className="mt-1 text-xs text-slate-500">or click below · max 15 MB</div>
              <Input
                ref={fileRef}
                type="file"
                className="hidden"
                accept=".pdf,.docx,.txt,image/*"
                onChange={(e) => onSelectFile(e.target.files?.[0])}
                data-testid="ai-extract-file-input"
              />
              <div className="mt-4 flex items-center justify-center gap-2">
                <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="ai-extract-choose-btn">
                  Choose file
                </Button>
                <Button
                  className="btn-primary-blue"
                  onClick={extract}
                  disabled={!file || extracting}
                  data-testid="ai-extract-run-btn"
                >
                  {extracting ? <><ArrowsClockwise size={14} className="mr-1 animate-spin" /> Extracting…</> : <><Sparkle size={14} className="mr-1" /> Extract with AI</>}
                </Button>
              </div>
              {file && !extracting && (
                <div className="mt-3 inline-flex items-center gap-2 text-xs text-slate-600">
                  <FileText size={12} /> {file.name} · {(file.size / 1024).toFixed(0)} KB
                </div>
              )}
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50/70 p-3 text-xs text-slate-600">
              <strong className="text-slate-800">Powered by Claude Sonnet 4.5.</strong> Extracted fields will be
              shown for review before anything is saved to {MODULE_LABEL[module] || module}.
            </div>
          </div>
        )}

        {step === "review" && draft && (
          <div className="space-y-4">
            <div className="flex items-start justify-between rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
              <div className="flex items-start gap-2 text-sm">
                <CheckCircle size={18} weight="fill" className="mt-0.5 text-emerald-600" />
                <div>
                  <div className="font-medium text-emerald-900">Extraction complete — human check required</div>
                  {draft.summary && <div className="text-xs text-emerald-800/90">{draft.summary}</div>}
                  {draft.verification_note && (
                    <div className="mt-0.5 text-[11px] text-emerald-700/80" data-testid="ai-verification-note">{draft.verification_note}</div>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700/70">AI confidence</div>
                <div className="font-display text-xl font-bold text-emerald-700">
                  {draft.confidence ?? "—"}<span className="text-xs">%</span>
                </div>
                <div className="text-[9px] text-emerald-700/60">capped until verified</div>
              </div>
            </div>

            {(draft.high_risk_fields || []).length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50/70 px-3 py-2 text-xs text-amber-800" data-testid="ai-highrisk-banner">
                <strong>{(draft.high_risk_fields || []).length} high-risk field(s)</strong> (bank details, values, registration numbers, dates)
                are highlighted below — tick each one after checking it against the source document.
              </div>
            )}

            <div className={`grid grid-cols-1 gap-4 ${draft.source_text || (file && file.type?.startsWith("image/")) ? "lg:grid-cols-2" : ""}`}>
              {(draft.source_text || (file && file.type?.startsWith("image/"))) && showSource && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="ai-source-panel">
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
                      Source · {draft.filename || file?.name}
                    </span>
                    <button type="button" className="text-[10px] text-slate-400 hover:text-slate-600" onClick={() => setShowSource(false)}>hide</button>
                  </div>
                  {file && file.type?.startsWith("image/") ? (
                    <img src={URL.createObjectURL(file)} alt="source document" className="max-h-[420px] w-full rounded object-contain" />
                  ) : (
                    <pre className="max-h-[420px] overflow-y-auto whitespace-pre-wrap text-[11px] leading-relaxed text-slate-600">{draft.source_text}</pre>
                  )}
                </div>
              )}
              <div>
                {!showSource && (draft.source_text || (file && file.type?.startsWith("image/"))) && (
                  <button type="button" className="mb-2 text-[10px] text-[#1E5CBA] hover:underline" onClick={() => setShowSource(true)}>
                    Show source document
                  </button>
                )}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {currentFields.map((k) => {
                    const multi = MULTILINE_FIELDS.has(k);
                    const selectKey = `${module}.${k}`;
                    const options = SELECT_OPTIONS[selectKey];
                    const isRisk = (draft.high_risk_fields || []).includes(k);
                    const hasValue = (draft.extracted_fields?.[k] ?? "") !== "";
                    const isVerified = verified.has(k);
                    const riskClass = isRisk && hasValue
                      ? (isVerified ? "rounded-md border border-emerald-300 bg-emerald-50/40 p-2"
                                    : "rounded-md border border-amber-300 bg-amber-50/60 p-2")
                      : "";
                    return (
                      <div key={k} className={`${multi ? "sm:col-span-2" : ""} ${riskClass}`}>
                        <div className="flex items-center justify-between">
                          <Label className="mb-1 block text-xs font-medium text-slate-600">
                            {FIELD_LABELS[k] || k.replace(/_/g, " ")}
                            {isRisk && hasValue && (
                              <span className={`ml-1.5 rounded px-1 text-[9px] font-semibold uppercase ${isVerified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                                {isVerified ? "verified" : "high risk"}
                              </span>
                            )}
                          </Label>
                          {isRisk && hasValue && (
                            <label className="flex cursor-pointer items-center gap-1 text-[10px] text-slate-500">
                              <input type="checkbox" checked={isVerified}
                                     onChange={(e) => setVerified((s) => {
                                       const n = new Set(s);
                                       e.target.checked ? n.add(k) : n.delete(k);
                                       return n;
                                     })}
                                     data-testid={`ai-verify-${k}`} />
                              checked
                            </label>
                          )}
                        </div>
                        {options ? (
                          <Select
                            value={draft.extracted_fields?.[k] || ""}
                            onValueChange={(v) => updateField(k, v)}
                          >
                            <SelectTrigger data-testid={`ai-extract-field-${k}`}>
                              <SelectValue placeholder="Select…" />
                            </SelectTrigger>
                            <SelectContent>
                              {options.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : multi ? (
                          <Textarea
                            rows={3}
                            value={draft.extracted_fields?.[k] ?? ""}
                            onChange={(e) => updateField(k, e.target.value)}
                            data-testid={`ai-extract-field-${k}`}
                          />
                        ) : (
                          <Input
                            type={k.endsWith("_date") ? "date" : (NUMERIC_FIELDS.has(k) ? "number" : "text")}
                            value={draft.extracted_fields?.[k] ?? ""}
                            onChange={(e) => updateField(k, e.target.value)}
                            data-testid={`ai-extract-field-${k}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Projects: structured deliverables editor */}
            {module === "projects" && !isRenew && (
              <DeliverablesEditor
                items={draft.extracted_fields?.deliverables || []}
                setItems={(v) => updateField("deliverables", v)}
              />
            )}
          </div>
        )}

        <DialogFooter>
          {step === "review" ? (
            <div className="flex w-full flex-col-reverse justify-between gap-2 sm:flex-row">
              <Button
                variant="outline"
                onClick={() => { setDraft(null); setStep("upload"); }}
                data-testid="ai-extract-back-btn"
              >
                <ArrowLeft size={14} className="mr-1" /> {isRenew ? "Upload new document" : "Upload another"}
              </Button>
              <div className="flex items-center gap-2">
                {!isRenew && (
                  <Button variant="outline" onClick={discard} data-testid="ai-extract-discard-btn">
                    <Trash size={14} className="mr-1" /> Discard
                  </Button>
                )}
                <Button
                  onClick={save}
                  disabled={saving}
                  className="btn-primary-blue"
                  data-testid="ai-extract-save-btn"
                >
                  <FloppyDisk size={14} className="mr-1" />
                  {saving
                    ? "Saving…"
                    : isRenew
                      ? "Save renewal"
                      : (saveLabel || `Save to ${MODULE_LABEL[module] || module}`)}
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
